import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, PencilLine, Trash2, User, Phone, Mail, Globe, Handshake, ChevronRight, Check, CalendarClock, Paperclip, Send, DollarSign, Building2, Activity } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import LeadForm from "../components/leads/LeadForm";
import ProposalsPanel from "../components/leads/ProposalsPanel";
import { LEAD_STATUSES, LEAD_TERMINAL, LEAD_STATUS_COLORS, LEAD_PRIORITY_COLORS } from "../components/leads/leadUtils";
import { formatMoney } from "../lib/currency";
import { useConfirm } from "../components/ConfirmProvider";

export default function LeadDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [followupInput, setFollowupInput] = useState("");

  const canEdit = ["admin", "manager", "ceo", "sales_head", "sales_member"].includes(user?.role);
  const canDelete = ["admin", "manager", "ceo"].includes(user?.role);

  const load = async () => {
    try {
      const [d, u] = await Promise.all([
        api.get(`/leads/${id}`),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setData(d.data);
      setUsers(u.data);
      setFollowupInput(d.data.lead.next_followup_date || "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      if (e.response?.status === 404 || e.response?.status === 403) navigate("/leads");
    }
  };
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="p-8 text-neutral-500">Loading…</div>;
  const { lead, activity } = data;
  const nameOf = (uid) => users.find(u => u.id === uid)?.name || "—";
  const currentIdx = LEAD_STATUSES.indexOf(lead.status);
  const isTerminal = LEAD_TERMINAL.includes(lead.status);

  const advanceStatus = async (status) => {
    const prev = lead.status;
    setData(d => ({ ...d, lead: { ...d.lead, status } }));
    try {
      await api.put(`/leads/${id}`, { status });
      toast.success(`Moved to ${status}`);
      load();
    } catch (e) {
      setData(d => ({ ...d, lead: { ...d.lead, status: prev } }));
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveFollowup = async () => {
    if (followupInput && lead.lead_date && followupInput < lead.lead_date) { toast.error("Follow-up date cannot be earlier than the lead date."); return; }
    try {
      await api.put(`/leads/${id}`, { next_followup_date: followupInput });
      toast.success("Follow-up saved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/leads/${id}/comments`, { body: comment });
      setComment("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async () => {
    if (!(await confirm({ title: "Are you sure you want to delete this lead?", description: "Its proposals and activity will be removed too." }))) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Deleted");
      navigate("/leads");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const convert = async () => {
    if (!(await confirm({ title: "Convert this lead to a Client?", description: "This will create a new Client record using this lead's details.", confirmText: "Convert", destructive: false }))) return;
    try {
      const { data } = await api.post(`/leads/${id}/convert`);
      if (data.already_converted) toast.success("Already converted — opening client");
      else toast.success(`Converted to client: ${data.client_name}`);
      navigate(`/clients/${data.client_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1600px]">
      <Link to="/leads" className="inline-flex items-center gap-2 text-xs font-mono uppercase text-neutral-600 hover:text-neutral-800"><ArrowLeft className="w-3 h-3" /> All Leads</Link>

      <div className="panel p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-orange-500/10 blur-3xl" />
        <div className="absolute inset-0 grid-lines opacity-20" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-6 flex-wrap">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500/40 to-red-500/30 border border-orange-500/30 grid place-items-center">
              <Handshake className="w-8 h-8" />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase text-neutral-500">{lead.lead_id} · {lead.lead_source}</div>
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1">{lead.lead_name}</h1>
              <div className="text-neutral-600 text-sm mt-1">{lead.company || "—"} · {Array.isArray(lead.service_required) ? (lead.service_required.join(" · ") || "—") : (lead.service_required || "—")}</div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${LEAD_STATUS_COLORS[lead.status] || ""}`}>{lead.status}</span>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${LEAD_PRIORITY_COLORS[lead.priority] || ""}`}>{lead.priority} priority</span>
                {lead.lead_value !== undefined && <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 flex items-center gap-1" data-testid="lead-value-badge">{formatMoney(lead.lead_value, lead.currency)}</span>}
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {lead.status === "Won" && !lead.converted_client_id && (
              <Button size="sm" onClick={convert} className="bg-emerald-500 hover:bg-emerald-600 text-neutral-900" data-testid="convert-lead-btn">
                <Building2 className="w-4 h-4 mr-1" /> Convert to Client
              </Button>
            )}
            {lead.converted_client_id && (
              <Link to={`/clients/${lead.converted_client_id}`} data-testid="open-converted-client">
                <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-700">
                  <Building2 className="w-4 h-4 mr-1" /> View Client
                </Button>
              </Link>
            )}
            {canEdit && (
              <Button size="sm" variant="outline" className="border-[#E4E7EC]" onClick={() => setEditOpen(true)} data-testid="edit-lead-btn">
                <PencilLine className="w-4 h-4 mr-1" /> Edit
              </Button>
            )}
            {canDelete && (
              <Button size="sm" variant="ghost" className="text-rose-400 hover:text-rose-700" onClick={remove} data-testid="delete-lead-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Pipeline */}
      <div className="panel p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-orange-400">Sales Pipeline</div>
            <div className="text-sm text-neutral-600" data-testid="lead-current-stage">Stage {Math.max(currentIdx + 1, 1)} of {LEAD_STATUSES.length} · <span className="font-semibold text-orange-700">{lead.status}</span></div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {LEAD_STATUSES.map((s, i) => {
            const done = (!isTerminal || lead.status === "Won") && i < currentIdx;
            const current = i === currentIdx;
            return (
              <button
                key={s}
                disabled={!canEdit}
                onClick={() => canEdit && advanceStatus(s)}
                data-testid={`lead-stage-${i}`}
                className={`text-[10px] font-mono px-2.5 py-1.5 rounded-lg border flex items-center gap-1.5 transition-colors ${
                  current ? "bg-orange-500 border-orange-500 text-white font-semibold" :
                  done ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-700" :
                  "bg-[#FFFFFF] border-[#E4E7EC] text-neutral-600"
                } ${canEdit ? "hover:border-orange-500/40 cursor-pointer" : "cursor-default"}`}>
                {done && <Check className="w-3 h-3" />}
                {s}
                {i < LEAD_STATUSES.length - 1 && <ChevronRight className="w-3 h-3 opacity-40 ml-1" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Meta grid */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="panel p-5 space-y-3">
          <div className="text-[10px] font-mono uppercase text-neutral-500">Contact</div>
          {lead.contact_person && <div className="flex items-center gap-2 text-sm"><User className="w-3.5 h-3.5 text-orange-400" /> {lead.contact_person}</div>}
          {lead.phone && <div className="flex items-center gap-2 text-sm"><Phone className="w-3.5 h-3.5 text-orange-400" /> {lead.phone}</div>}
          {lead.email && <div className="flex items-center gap-2 text-sm"><Mail className="w-3.5 h-3.5 text-orange-400" /> <a href={`mailto:${lead.email}`} className="hover:text-orange-700">{lead.email}</a></div>}
          {lead.website && <div className="flex items-center gap-2 text-sm"><Globe className="w-3.5 h-3.5 text-orange-400" /> <a href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`} target="_blank" rel="noreferrer" className="hover:text-orange-700 truncate">{lead.website}</a></div>}
          {!lead.contact_person && !lead.phone && !lead.email && !lead.website && <div className="text-xs text-neutral-500">No contact info</div>}
        </div>

        <div className="panel p-5 space-y-3">
          <div className="text-[10px] font-mono uppercase text-neutral-500">Ownership</div>
          <div className="text-[11px] font-mono"><span className="text-neutral-500">Assigned:</span> <span className="text-neutral-800">{nameOf(lead.assigned_to)}</span></div>
          <div className="text-[11px] font-mono"><span className="text-neutral-500">Head:</span> <span className="text-neutral-800">{nameOf(lead.head_id)}</span></div>
          <div className="text-[11px] font-mono"><span className="text-neutral-500">Lead Date:</span> <span className="text-neutral-800">{lead.lead_date || "—"}</span></div>
          <div className="text-[11px] font-mono"><span className="text-neutral-500">Source:</span> <span className="text-neutral-800">{lead.lead_source}</span></div>
        </div>

        <div className="panel p-5 space-y-3" data-testid="followup-panel">
          <div className="text-[10px] font-mono uppercase text-neutral-500 flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Next Follow-up</div>
          <div className="flex items-center gap-2">
            <Input type="date" min={lead.lead_date || undefined} value={followupInput} onChange={e => setFollowupInput(e.target.value)} disabled={!canEdit} className="bg-[#FFFFFF] border-[#E4E7EC] text-sm" data-testid="lead-followup-input" />
            <Button size="sm" onClick={saveFollowup} disabled={!canEdit} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="lead-followup-save">Save</Button>
          </div>
          <div className="text-[11px] font-mono"><span className="text-neutral-500">Last Contact:</span> <span className="text-neutral-800">{lead.last_contact_date || "—"}</span></div>
        </div>
      </div>

      {/* Notes + Attachments */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="panel p-5">
          <div className="text-[10px] font-mono uppercase text-neutral-500 mb-2">Notes</div>
          <div className="text-sm text-neutral-800 whitespace-pre-wrap">{lead.notes || <span className="text-neutral-500">No notes.</span>}</div>
        </div>
        <div className="panel p-5">
          <div className="text-[10px] font-mono uppercase text-neutral-500 mb-2 flex items-center gap-1"><Paperclip className="w-3 h-3" /> Attachments</div>
          {(lead.attachments || []).length === 0 && <div className="text-sm text-neutral-500">No attachments.</div>}
          <div className="space-y-1">
            {(lead.attachments || []).map((a, i) => (
              <a key={i} href={fileUrl(a.path)} target="_blank" rel="noreferrer" data-testid={`lead-att-${i}`} className="text-[11px] font-mono flex items-center gap-2 text-orange-700 hover:text-orange-700 truncate">
                <Paperclip className="w-3 h-3" /> {a.filename}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Proposals */}
      <ProposalsPanel leadId={id} canEdit={canEdit} canDelete={canDelete} onChanged={load} />

      {/* Activity + comment */}
      <div className="panel p-5">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-orange-400" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-orange-400">Activity</div>
            <div className="text-sm text-neutral-600">{activity.length} events</div>
          </div>
        </div>

        {canEdit && (
          <div className="flex gap-2 mb-4">
            <Textarea rows={1} value={comment} onChange={e => setComment(e.target.value)} placeholder="Log a call, note, or update..." className="bg-[#FFFFFF] border-[#E4E7EC] text-sm" data-testid="lead-comment-input" />
            <Button onClick={addComment} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="lead-comment-post">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        )}

        {activity.length === 0 ? (
          <div className="text-xs text-neutral-500">No activity yet.</div>
        ) : (
          <div className="relative pl-6">
            <div className="absolute left-[7px] top-1 bottom-1 w-px bg-[#E4E7EC]" />
            {activity.map(it => (
              <div key={it.id} className="relative mb-3">
                <div className="absolute -left-6 top-1 w-3 h-3 rounded-full bg-gradient-to-br from-orange-500 to-red-500 border-2 border-[#FFFFFF]" />
                <div className="rounded-lg border border-[#E4E7EC] bg-[#FFFFFF] p-3">
                  <div className="flex items-center gap-2 text-[11px] font-mono text-neutral-600 flex-wrap">
                    <span className="text-neutral-800">{it.actor_name}</span>
                    <span className="text-neutral-600">·</span>
                    <span className="text-neutral-500 uppercase">{it.actor_role}</span>
                    <span className="text-neutral-600">·</span>
                    <span className="text-neutral-500">{new Date(it.created_at).toLocaleString()}</span>
                  </div>
                  <div className="mt-1 text-sm">
                    {it.action}
                    {it.detail && <span className="text-neutral-600 ml-1">— {it.detail}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <LeadForm open={editOpen} onOpenChange={setEditOpen} initial={lead} users={users} currentUser={user} onSaved={load} />
    </div>
  );
}
